import { useMemo, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import {
  CeldaVigencia,
  EmptyState,
  Icon,
  ProgressBar,
  StatusBadge,
  VigenciaModal,
} from "@/components/ds";
import { AlertCircle, Loader2 } from "@/components/ds/icons";
import { CabeceraGrupoDocumentos } from "@/components/documentos/CabeceraGrupoDocumentos";
import { DocumentoFilaAcciones } from "@/components/documentos/DocumentoFilaAcciones";
import { ConfigurarExigiblesModal } from "@/components/persona-documentos/ConfigurarExigiblesModal";
import { cn } from "@/lib/utils";
import { ordenarPorUrgencia, type SlotDocumentacion } from "@/lib/persona-documentos-resumen";
import { useDocumentacionGeneral } from "@/hooks/useDocumentacionGeneral";

/**
 * Tab "Documentación General" de la Ficha del trabajador. Reemplaza a
 * `DocumentosAdicionalesPanel`, que dibujaba los 48 tipos del catálogo como
 * filas idénticas con un botón "Subir" — sin estado, sin vigencia visible y
 * sin ningún orden que pusiera adelante lo que hay que resolver.
 *
 * Estructura del prototipo (`Gestión de Capacitaciones.dc.html`, pantalla
 * "Ficha del trabajador"): tarjeta de progreso con buscador arriba, y debajo
 * los documentos separados en dos grupos —lo que falta primero, lo que está
 * cargado después—. Las dos secciones que el prototipo tiene además de esas
 * ("No exigidos por tu empresa" y "No aplican a este trabajador") NO se
 * implementan: dependen de dos features que el backend no tiene —una
 * configuración por empresa de qué tipos pide, y una marca "No aplica" por
 * trabajador—. Ver la nota de copy más abajo.
 *
 * La carga y las mutaciones viven en `useDocumentacionGeneral`; acá queda el
 * layout y el estado que es sólo de esta pantalla (búsqueda, modales).
 */

/** Porcentaje entero, con el 0/0 resuelto como 0 en vez de NaN. */
function pct(valor: number, total: number): number {
  return total <= 0 ? 0 : Math.round((valor / total) * 100);
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** `uploaded_at` viene con hora (ISO completo), así que `new Date` alcanza. */
function formatFechaSubida(iso: string): string {
  return new Date(iso).toLocaleDateString("es-CL", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/** Etiqueta del badge. `subido` se rotula "Vigente": lo que importa no es que
 * alguien haya subido el archivo, sino que siga valiendo. Los otros tres
 * estados ya se llaman como corresponde en el DS. */
function etiquetaEstado(slot: SlotDocumentacion): string | undefined {
  return slot.estado === "subido" ? "Vigente" : undefined;
}

/** Grilla compartida por las filas de "Con documento cargado". Envuelve a una
 * columna bajo `sm` en vez de comprimirse (regla de grillas del DS). */
const GRID_CARGADO = "sm:grid-cols-[minmax(180px,1fr)_112px_minmax(120px,148px)_100px]";
/** Ídem para "Sin documento cargado": nombre + acción. */
const GRID_SIN_CARGAR = "sm:grid-cols-[minmax(180px,1fr)_auto]";

export interface DocumentacionGeneralPanelProps {
  personaUuid: string;
  /** Correo del trabajador — recibe copia de los avisos de vencimiento. */
  personaEmail?: string | null;
  /**
   * Ventana de "por vencer" del servidor (`meta.dias_por_vencer` de
   * `GET /personas/resumen`). Es lo que mantiene alineados el badge de la fila
   * y el KPI "Documentación" del encabezado, que se calcula server-side.
   */
  diasPorVencer?: number;
  /** Avisa a la Ficha que recargue sus KPI: salen del servidor, no de acá. */
  onDocumentosChange?: () => void;
  /** Dotación de la empresa — el modal de exigibles la usa para decir a
   * cuántas personas alcanza el cambio. */
  dotacion?: number;
}

export function DocumentacionGeneralPanel({
  personaUuid,
  personaEmail,
  diasPorVencer,
  onDocumentosChange,
  dotacion,
}: DocumentacionGeneralPanelProps) {
  const documentacion = useDocumentacionGeneral(personaUuid, {
    diasPorVencer,
    onChange: onDocumentosChange,
  });

  const {
    slots,
    resumen,
    isLoading,
    loadError,
    recargar,
    busyTipoId,
    uploadingPercents,
    subir,
    cancelarSubida,
    descargar,
    eliminar,
    guardarVigencia,
    guardarExigidos,
  } = documentacion;

  const [q, setQ] = useState("");
  const [configAbierto, setConfigAbierto] = useState(false);
  const [noExigidosAbierto, setNoExigidosAbierto] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<SlotDocumentacion | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [vigenciaTarget, setVigenciaTarget] = useState<SlotDocumentacion | null>(null);

  const filtrados = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return slots;
    return slots.filter(
      (s) =>
        s.tipo.label.toLowerCase().includes(query) || s.tipo.grupo.toLowerCase().includes(query),
    );
  }, [slots, q]);

  // Mientras la empresa no configure nada, TODO el catálogo se trata como
  // antes (dos grupos por presencia de archivo): no hay declaración contra la
  // cual separar "exigido" de "no exigido", y fabricar esa división sola sería
  // inventarle una exigencia a la empresa.
  const separaPorExigencia = resumen.exigiblesConfigurados;

  const relevantes = useMemo(
    () => (separaPorExigencia ? filtrados.filter((s) => s.tipo.exigido) : filtrados),
    [filtrados, separaPorExigencia],
  );

  const conDocumento = useMemo(
    () => ordenarPorUrgencia(relevantes.filter((s) => s.documento !== null)),
    [relevantes],
  );
  const sinDocumento = useMemo(() => relevantes.filter((s) => s.documento === null), [relevantes]);

  /** Lo que la empresa NO pide. Se puede subir igual —a veces un trabajador
   * trae un certificado que no se le exigía— pero no cuenta en el porcentaje.
   * Va colapsado: es la sección que menos se mira. */
  const noExigidos = useMemo(
    () => (separaPorExigencia ? filtrados.filter((s) => !s.tipo.exigido) : []),
    [filtrados, separaPorExigencia],
  );

  async function handleConfirmDelete() {
    if (!deleteTarget?.documento) return;
    setIsDeleting(true);
    try {
      await eliminar(deleteTarget.tipo, deleteTarget.documento);
      setDeleteTarget(null);
    } finally {
      setIsDeleting(false);
    }
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  if (loadError) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
          <AlertCircle className="text-destructive h-6 w-6" />
          <p className="text-muted-foreground text-sm">{loadError}</p>
          <Button variant="outline" size="sm" onClick={recargar}>
            Reintentar
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (slots.length === 0) {
    return (
      <Card>
        <CardContent className="py-4">
          <EmptyState
            icono="folder_off"
            mensaje="Todavía no hay tipos de documento en el catálogo de tu empresa."
          />
        </CardContent>
      </Card>
    );
  }

  const sinResultados = filtrados.length === 0;

  return (
    <div className="flex flex-col gap-3">
      {/* Tarjeta de progreso.

          El DENOMINADOR es `exigibles`, la declaración explícita de la empresa
          en "Documentos que exige tu empresa" — nunca el catálogo entero. Medir
          contra los 48 tipos convertiría un checklist opcional en 48
          incumplimientos aparentes para todo el mundo.

          Y mientras la empresa NO configuró nada no se muestra porcentaje: no
          hay denominador legítimo todavía. En ese estado la tarjeta habla del
          catálogo como referencia y ofrece configurarlo, en vez de inventar una
          exigencia que nadie declaró. */}
      <div className="bg-card flex flex-wrap items-start gap-4 rounded-xl border px-4 py-3.5">
        <div className="flex min-w-[220px] flex-1 flex-col gap-2">
          {!resumen.exigiblesConfigurados ? (
            <>
              <span className="fc-section-title">Todavía no definiste qué documentos pides</span>
              <span className="text-muted-foreground text-xs">
                El catálogo tiene <span className="fc-num">{resumen.catalogo}</span> tipos y no
                todos aplican a cada empresa. Marcá cuáles pedís y recién ahí tiene sentido medir
                cuánto falta.
              </span>
            </>
          ) : resumen.exigibles === 0 ? (
            <>
              <span className="fc-section-title">No estás exigiendo ningún documento</span>
              <span className="text-muted-foreground text-xs">
                Los <span className="fc-num">{resumen.cargados}</span> documentos cargados siguen
                disponibles, pero no hay nada que medir.
              </span>
            </>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
                <span className="fc-section-title">
                  <span className="fc-num">{pct(resumen.exigiblesCargados, resumen.exigibles)}%</span>{" "}
                  cargado
                </span>
                <span className="text-muted-foreground text-xs">
                  <span className="fc-num">{resumen.exigiblesCargados}</span> de{" "}
                  <span className="fc-num">{resumen.exigibles}</span> documentos exigibles cargados
                  {resumen.exigiblesSinCargar > 0 && (
                    <>
                      {" · "}
                      <span className="fc-num">{resumen.exigiblesSinCargar}</span> sin subir
                    </>
                  )}
                  {resumen.porVencer > 0 && (
                    <>
                      {" · "}
                      <span className="text-warning">
                        <span className="fc-num">{resumen.porVencer}</span> por vencer
                      </span>
                    </>
                  )}
                  {resumen.vencidos > 0 && (
                    <>
                      {" · "}
                      <span className="text-destructive">
                        <span className="fc-num">{resumen.vencidos}</span>{" "}
                        {resumen.vencidos === 1 ? "vencido" : "vencidos"}
                      </span>
                    </>
                  )}
                </span>
              </div>
              <ProgressBar
                valor={resumen.exigiblesCargados}
                total={resumen.exigibles}
                alto={7}
                className="max-w-[420px]"
              />
            </>
          )}
          <span className="text-soft text-[11.5px] leading-[1.45]">
            <span className="fc-num">{resumen.exigibles}</span> de{" "}
            <span className="fc-num">{resumen.catalogo}</span> tipos de documento exigidos por tu
            empresa ·{" "}
            <button
              type="button"
              onClick={() => setConfigAbierto(true)}
              className="text-primary-hover decoration-primary-border underline underline-offset-2"
            >
              configurar exigibles
            </button>
          </span>
        </div>

        <div className="relative min-w-[190px] flex-none">
          <Icon
            name="search"
            size={16}
            className="text-icon-soft absolute top-1/2 left-2.5 -translate-y-1/2"
          />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar documento…"
            aria-label="Buscar documento"
            className="h-[34px] pl-8 text-xs"
          />
        </div>
      </div>

      {sinResultados ? (
        <div className="bg-card rounded-xl border">
          <EmptyState mensaje="Ningún documento coincide con la búsqueda." />
        </div>
      ) : (
        <>
          {conDocumento.length > 0 && (
            <section className="bg-card overflow-hidden rounded-xl border">
              <CabeceraGrupoDocumentos
                icono="task"
                titulo="Con documento cargado"
                cuenta={conDocumento.length}
                tono="neutro"
              />
              {conDocumento.map((slot) => {
                const doc = slot.documento!;
                const uploading = slot.tipo.id in uploadingPercents;
                return (
                  <div
                    key={slot.tipo.id}
                    className={cn(
                      "border-border-faint grid gap-2 border-b px-4 py-2.5 last:border-b-0",
                      "hover:bg-primary-50 transition-colors sm:items-center sm:gap-3",
                      GRID_CARGADO,
                    )}
                  >
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <span className="fc-cell fc-truncate">{slot.tipo.label}</span>
                      <span className="fc-meta fc-truncate">
                        {slot.tipo.grupo} · {formatBytes(doc.size_bytes)} · subido{" "}
                        {formatFechaSubida(doc.uploaded_at)}
                      </span>
                    </div>

                    <div className="flex min-w-0 items-center">
                      {uploading ? (
                        <div className="w-full max-w-[100px]">
                          <Progress value={uploadingPercents[slot.tipo.id] ?? 0} className="h-1.5" />
                        </div>
                      ) : (
                        <StatusBadge estado={slot.estado}>{etiquetaEstado(slot)}</StatusBadge>
                      )}
                    </div>

                    {/* La celda ENTERA abre el modal —es el objetivo más grande
                        y la vigencia es el dato que más se corrige— pero lleva
                        un lápiz visible al lado de la fecha. El tooltip solo no
                        alcanzaba: aparece recién después de que alguien decidió
                        apuntar ahí, así que no sirve para descubrir que la celda
                        es editable. El ícono sí, y no compite con las acciones
                        de la derecha porque va en gris de ícono y se satura al
                        pasar por encima. */}
                    <CeldaVigencia
                      label={slot.tipo.label}
                      fechaVencimiento={doc.fecha_vencimiento}
                      avisos={doc.notificar_dias?.length ?? 0}
                      estado={slot.vigencia}
                      onEditar={() => setVigenciaTarget(slot)}
                      ocupada={busyTipoId === slot.tipo.id}
                      className="w-full"
                    />

                    <DocumentoFilaAcciones
                      label={slot.tipo.label}
                      tieneDocumento
                      busy={busyTipoId === slot.tipo.id}
                      uploading={uploading}
                      uploadPercent={uploadingPercents[slot.tipo.id] ?? 0}
                      onPickFile={(file) => subir(slot.tipo, file)}
                      onCancelUpload={() => cancelarSubida(slot.tipo.id)}
                      onDownload={() => descargar(slot.tipo, doc)}
                      onRequestDelete={() => setDeleteTarget(slot)}
                    />
                  </div>
                );
              })}
            </section>
          )}

          {sinDocumento.length > 0 && (
            <section className="bg-card overflow-hidden rounded-xl border">
              <CabeceraGrupoDocumentos
                icono="pending_actions"
                titulo="Sin documento cargado"
                cuenta={sinDocumento.length}
                tono="warning"
              />
              {sinDocumento.map((slot) => {
                const uploading = slot.tipo.id in uploadingPercents;
                return (
                  <div
                    key={slot.tipo.id}
                    className={cn(
                      "border-border-faint grid gap-2 border-b px-4 py-2.5 last:border-b-0",
                      "hover:bg-primary-50 transition-colors sm:items-center sm:gap-3",
                      GRID_SIN_CARGAR,
                    )}
                  >
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <span className="fc-cell fc-truncate">{slot.tipo.label}</span>
                      <span className="fc-meta fc-truncate">{slot.tipo.grupo}</span>
                    </div>
                    <DocumentoFilaAcciones
                      label={slot.tipo.label}
                      tieneDocumento={false}
                      busy={busyTipoId === slot.tipo.id}
                      uploading={uploading}
                      uploadPercent={uploadingPercents[slot.tipo.id] ?? 0}
                      onPickFile={(file) => subir(slot.tipo, file)}
                      onCancelUpload={() => cancelarSubida(slot.tipo.id)}
                      // Sin documento no hay nada que ver ni que borrar; las
                      // dos acciones no se renderizan en esta variante.
                      onDownload={() => {}}
                      onRequestDelete={() => {}}
                    />
                  </div>
                );
              })}
            </section>
          )}
        </>
      )}

      {noExigidos.length > 0 && (
        <section className="bg-card overflow-hidden rounded-xl border">
          <button
            type="button"
            onClick={() => setNoExigidosAbierto((v) => !v)}
            className="hover:bg-surface-muted flex w-full items-center gap-2.5 px-4 py-3 text-left transition-colors"
          >
            <Icon name="folder_open" size={17} className="text-icon-soft" />
            <span className="text-muted-foreground flex-1 text-[13px] font-semibold">
              No exigidos por tu empresa
            </span>
            <span className="text-soft text-[11.5px]">
              <span className="fc-num">{noExigidos.length}</span> tipos · no cuentan en el
              porcentaje
            </span>
            <Icon
              name="expand_more"
              size={19}
              className={cn("text-icon-soft transition-transform", noExigidosAbierto && "rotate-180")}
            />
          </button>

          {noExigidosAbierto && (
            <div className="border-border-subtle border-t">
              {noExigidos.map((slot) => (
                <div
                  key={slot.tipo.id}
                  className={cn(
                    "border-border-faint grid gap-2 border-b px-4 py-2.5 last:border-b-0",
                    "hover:bg-primary-50 transition-colors sm:items-center sm:gap-3",
                    GRID_SIN_CARGAR,
                  )}
                >
                  <div className="flex min-w-0 flex-col gap-0.5">
                    <span className="fc-truncate text-[12.5px]">{slot.tipo.label}</span>
                    <span className="text-soft fc-truncate text-[11px]">
                      {slot.tipo.grupo}
                      {slot.documento ? " · ya tiene documento" : " · no cuenta en el porcentaje"}
                    </span>
                  </div>
                  <DocumentoFilaAcciones
                    label={slot.tipo.label}
                    tieneDocumento={slot.documento !== null}
                    busy={busyTipoId === slot.tipo.id}
                    uploading={slot.tipo.id in uploadingPercents}
                    uploadPercent={uploadingPercents[slot.tipo.id] ?? 0}
                    onPickFile={(file) => subir(slot.tipo, file)}
                    onCancelUpload={() => cancelarSubida(slot.tipo.id)}
                    onDownload={() => slot.documento && descargar(slot.tipo, slot.documento)}
                    onRequestDelete={() => slot.documento && setDeleteTarget(slot)}
                  />
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      <ConfigurarExigiblesModal
        open={configAbierto}
        onOpenChange={setConfigAbierto}
        tipos={slots.map((s) => s.tipo)}
        dotacion={dotacion}
        origen="ficha"
        onGuardar={guardarExigidos}
      />

      <VigenciaModal
        open={vigenciaTarget !== null}
        onOpenChange={(open) => !open && setVigenciaTarget(null)}
        label={vigenciaTarget?.tipo.label ?? ""}
        documento={vigenciaTarget?.documento ?? null}
        onSubmit={(opts) =>
          vigenciaTarget?.documento
            ? guardarVigencia(vigenciaTarget.tipo, vigenciaTarget.documento, opts)
            : Promise.resolve()
        }
        copia={personaEmail ? { email: personaEmail, etiqueta: "el trabajador" } : null}
      />

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar documento</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.documento ? (
                <>
                  Se eliminará <strong>{deleteTarget.documento.filename_original}</strong> de{" "}
                  <strong>{deleteTarget.tipo.label}</strong>. Esta acción no se puede deshacer.
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirmDelete();
              }}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Eliminar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
